import pick from 'lodash/pick';
import reduce from 'lodash/reduce';

import { setMealOptions } from 'reducers/dashboard/basketEdits/mealOptions';

export const mapBasketStateToParams = (basket, updatedBasket, selectedPairings) => ({
  weekly_basket: buildBasketResource(basket, updatedBasket, selectedPairings),
});

export const mapV3BasketStateToParams = (basket, updatedBasket, editStage) => ({
  weekly_basket: buildBasketResource(basket, updatedBasket),
  editStage,
});

export const mapBasketStateToReceiptParams = (
  basket,
  { selectedMealOptions = {}, selectedPairings = {}, ...updatedBasket },
  includeDiscounts,
  excludeMealPrices
) => {
  return {
    ...buildBasketResource(basket, { ...updatedBasket, selectedMealOptions, selectedPairings }),
    menu_id: basket.menu.id,
    meal_option_selections: transformMealOptionsByGroupId(selectedMealOptions),
    suggested_pairings: transformBasketEditedSelectedSuggestedPairings(selectedPairings),
    include_discounts: includeDiscounts,
    exclude_meal_prices: excludeMealPrices,
  };
};

export const mapV3BasketStateToReceiptParams = (
  basket,
  { selectedMealOptions = {}, selectedPairings = {}, ...updatedBasket },
  includeDiscounts,
  excludeMealPrices
) => {
  return {
    ...buildBasketResource(basket, { ...updatedBasket, selectedMealOptions, selectedPairings }),
    menu_id: basket.menu.id,
    meal_option_selections: transformMealOptionsByGroupId(selectedMealOptions),
    suggested_pairings: transformBasketEditedSelectedSuggestedPairings(selectedPairings),
    include_discounts: includeDiscounts,
    exclude_meal_prices: excludeMealPrices,
  };
};

const buildMeals = (mealIds, mealServings, selectedMealOptions) =>
  mealIds.map(mealId => ({
    meal_id: mealId,
    servings: mealServings[mealId],
    meal_option_ids: Object.values(selectedMealOptions[mealId] || {}),
  }));

const buildBasketResource = (
  basket,
  { deliveryDay, servings, mealServings = {}, selectedMealOptions = {}, selectedPairings = {} }
) => {
  const mealIds = Object.keys(mealServings);
  return {
    delivery_day: deliveryDay || basket.deliveryDay,
    servings: servings || basket.servings,
    meal_ids: mealIds,
    meals: buildMeals(mealIds, mealServings, selectedMealOptions),
    meal_servings: mealServings,
    suggested_pairings: transformBasketEditedSelectedSuggestedPairings(selectedPairings),
  };
};

/**
 * Transforms meal option selection objects from the API into an object
 * keyed by meal option group id
 */
export const transformMealOptions = (meals, mealOptionSelections) =>
  meals.reduce(
    (hash, meal) => ({
      ...hash,
      ...setMealOptions(mealOptionSelections, meal.id),
    }),
    {}
  );

/**
 * Transforms nested object of selected meal options keyed by meal id into an object
 * keyed by meal option group id
 * @param {Object<mealId, Object<mealOptionGroupId, mealOptionId>} mealOptions
 * @returns {Object<mealOptionGroupId, mealOptionId>}
 */
const transformMealOptionsByGroupId = mealOptions =>
  Object.values(mealOptions).reduce((obj, mealOptionGroup) => ({ ...obj, ...mealOptionGroup }), {});

/**
 * Transforms array of selected pairings state data (objects) into an array
 * of objects with key value pairs for host meal id and paired meal id
 * @param Array[Object{<hostMealId, Object<suggestedPairing>}]
 * @returns Array[Object<hostMealId, pairedMealId>]
 */
export const transformSelectedSuggestedPairings = suggestedPairings => {
  return reduce(
    suggestedPairings,
    (arr, pairing) => [
      ...arr,
      { hostMealId: pairing.hostMealId, pairedMealId: pairing.suggestedPairing.id },
    ],
    []
  );
};

/**
 * Transforms array of selected pairings state data (objects) into an array
 * of objects with key value pairs for host meal id and paired meal id
 * @param Object{<hostMealId suggestedPairing>}
 * @returns Array[Object<hostMealId, pairedMealId>]
 */
const transformBasketEditedSelectedSuggestedPairings = suggestedPairings => {
  return reduce(
    suggestedPairings,
    (arr, pairedMealId, hostMealId) => [
      ...arr,
      { hostMealId: hostMealId, pairedMealId: pairedMealId },
    ],
    []
  );
};

/**
 * Transforms meal option selection objects from the API into nested objects
 * keyed by both meal id and meal option group id
 * @param {Object<mealId, Array<mealOptionSelection>} mealOptionSelectionsByMeal
 * @param {string} mealId
 * @param {Object} mealOptionSelection
 * @param {string} mealOptionSelection.mealOptionGroupId
 * @param {string} mealOptionSelection.mealOptionId
 * @returns {Object<mealId, Object<mealOptionGroupId, mealOptionId>>}
 */
export const transformMealOptionSelections = mealOptionSelections => {
  const selections = {};
  Object.keys(mealOptionSelections).forEach(mealId => {
    const optionGroups = mealOptionSelections[mealId];
    if (optionGroups && optionGroups.length > 0) {
      selections[mealId] = optionGroups.reduce(
        (obj, group) => ({ ...obj, [group.mealOptionGroupId]: group.mealOptionId }),
        {}
      );
    }
  });
  return selections;
};

/**
 * Transforms array of meal servings state data into an array
 * of objects with key value pairs for meal id and servings
 * @param Object{<mealId => servings>}
 * @returns Array[Object<mealId, servings>]
 */
export const transformServingsToMealPriceParams = mealServings => {
  return Object.entries(mealServings).map(([meal_id, servings]) => ({ meal_id, servings }));
};

export const mapTasteProfileStateToParams = ({ allergies, preferences }, changes = {}) => {
  const allergyParams = allergies.map(allergy => ({
    ...pick(allergy, ['name', 'displayName']),
    avoid: changes[allergy.name] == null ? allergy.avoid : changes[allergy.name],
  }));

  const preferenceParams = preferences.map(preference => ({
    ...pick(preference, ['name', 'displayName']),
    interested: changes[preference.name] == null ? preference.interested : changes[preference.name],
  }));

  return { allergies: allergyParams, preferences: preferenceParams };
};
